import React from 'react';
import { Link as RouterLink, useOutletContext, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { palette } from '../../theme/colors';

interface PortalContext {
  portalInfo: {
    organization: {
      id: string;
      name: string;
      b2bPortalSettings?: {
        welcomeMessage?: string;
        termsUrl?: string;
        requestAccessEnabled?: boolean;
      };
    };
    settings: {
      welcomeMessage?: string;
      termsUrl?: string;
      requestAccessEnabled?: boolean;
    };
  };
  isAuthenticated: boolean;
}

const features = [
  {
    icon: <InventoryIcon sx={{ fontSize: 40 }} />,
    title: 'Wholesale Pricing',
    description: 'Access exclusive B2B pricing tiers based on your volume.',
  },
  {
    icon: <LocalShippingIcon sx={{ fontSize: 40 }} />,
    title: 'Bulk Orders',
    description: 'Request quotes for large quantity orders with custom terms.',
  },
  {
    icon: <SupportAgentIcon sx={{ fontSize: 40 }} />,
    title: 'Dedicated Support',
    description: 'Get priority support from our B2B team for all your needs.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
    title: 'Verified Quality',
    description: 'All products meet our strict quality standards.',
  },
];

export const B2BLandingPage: React.FC = () => {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const { portalInfo, isAuthenticated } = useOutletContext<PortalContext>();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 2,
          background: `linear-gradient(135deg, ${palette.primary}15 0%, ${palette.accent}10 100%)`,
          borderRadius: 4,
          mb: 6,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: 3,
            bgcolor: palette.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <BusinessIcon sx={{ color: '#fff', fontSize: 48 }} />
        </Box>

        <Typography variant="h3" fontWeight={800} color={palette.textPrimary} gutterBottom>
          Welcome to {portalInfo.organization.name}
        </Typography>
        <Typography variant="h6" color={palette.textSecondary} sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
          {portalInfo.settings?.welcomeMessage ||
            'Your trusted B2B partner. Access exclusive wholesale pricing and dedicated support.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {isAuthenticated ? (
            <Button
              component={RouterLink}
              to={`/b2b/${orgSlug}/products`}
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              Browse Products
            </Button>
          ) : (
            <>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/request-access`}
                variant="contained"
                size="large"
                sx={{ px: 4, py: 1.5 }}
              >
                Request Access
              </Button>
              <Button
                component={RouterLink}
                to={`/b2b/${orgSlug}/login`}
                variant="outlined"
                size="large"
                sx={{ px: 4, py: 1.5, borderColor: palette.border }}
              >
                Already a Partner? Login
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Features Grid */}
      <Typography variant="h5" fontWeight={700} color={palette.textPrimary} sx={{ mb: 3, textAlign: 'center' }}>
        Why Partner With Us?
      </Typography>
      <Grid container spacing={3} sx={{ mb: 6 }}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                },
              }}
            >
              <CardContent sx={{ py: 4 }}>
                <Box sx={{ color: palette.primary, mb: 2 }}>{feature.icon}</Box>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Stats */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 6,
          py: 4,
          borderTop: `1px solid ${palette.border}`,
          borderBottom: `1px solid ${palette.border}`,
          mb: 6,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={800} color={palette.primary}>
            100+
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Products Available
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={800} color={palette.primary}>
            24/7
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Support Available
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h3" fontWeight={800} color={palette.primary}>
            Fast
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Quote Response
          </Typography>
        </Box>
      </Box>

      {/* CTA Section */}
      {!isAuthenticated && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 4,
            bgcolor: palette.surface,
            borderRadius: 4,
            border: `1px solid ${palette.border}`,
          }}
        >
          <Typography variant="h5" fontWeight={700} color={palette.textPrimary} gutterBottom>
            Ready to Get Started?
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto' }}>
            Submit an access request and our team will review your application within 24-48 hours.
          </Typography>
          <Button
            component={RouterLink}
            to={`/b2b/${orgSlug}/request-access`}
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
          >
            Request B2B Access
          </Button>
        </Box>
      )}
    </Box>
  );
};
